// src/components/DevPasswordGate.tsx
// 開発環境へのアクセスを制限するパスワードゲート

import React, { useState, useEffect, useCallback } from 'react';

const DEV_PASSWORD_KEY = 'orifoli_dev_authenticated';
const DEV_PASSWORD_HASH_KEY = 'orifoli_dev_password_hash';
// SHA-256 hash of the password
// Set via environment variable VITE_DEV_PASSWORD_HASH
// To generate: echo -n "your-password" | shasum -a 256
// Default password: "orifoli-dev"
// Default hash: echo -n "orifoli-dev" | shasum -a 256
const EXPECTED_HASH = import.meta.env.VITE_DEV_PASSWORD_HASH || '1b9cc91309740fdbac25a9f0d7b2c79608ea9bf3dcf5afcf066e6e40cfa6bc51';

interface DevPasswordGateProps {
  children: React.ReactNode;
}

// Simple hash function for client-side validation
async function hashPassword(password: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(password);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

const DevPasswordGate: React.FC<DevPasswordGateProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Check if running in development environment
  const isDev = import.meta.env.MODE !== 'production' &&
                typeof window !== 'undefined' &&
                !window.location.hostname.includes('localhost');

  // Check if already authenticated
  useEffect(() => {
    if (!isDev) {
      setIsAuthenticated(true);
      return;
    }

    const checkAuth = async () => {
      const authenticated = localStorage.getItem(DEV_PASSWORD_KEY);
      const storedHash = localStorage.getItem(DEV_PASSWORD_HASH_KEY);

      if (authenticated === 'true' && storedHash === EXPECTED_HASH) {
        setIsAuthenticated(true);
      } else {
        setIsAuthenticated(false);
      }
    };

    checkAuth();
  }, [isDev]);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const hash = await hashPassword(password);

      if (hash === EXPECTED_HASH) {
        localStorage.setItem(DEV_PASSWORD_KEY, 'true');
        localStorage.setItem(DEV_PASSWORD_HASH_KEY, hash);
        setIsAuthenticated(true);
      } else {
        setError('パスワードが正しくありません');
      }
    } catch (err) {
      setError('認証に失敗しました');
    } finally {
      setIsLoading(false);
    }
  }, [password]);

  // Show loading state while checking authentication
  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-purple-950 via-purple-900 to-black">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
      </div>
    );
  }

  // Show password gate if not authenticated
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-purple-950 via-purple-900 to-black p-4">
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 max-w-sm w-full">
          <div className="text-center mb-6">
            <h1 className="text-2xl font-bold text-white mb-2">ORIFOLI DEV</h1>
            <p className="text-white/60 text-sm">開発環境へのアクセスにはパスワードが必要です</p>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="パスワードを入力"
                className="w-full px-4 py-3 bg-white/20 border border-white/30 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-purple-400"
                autoFocus
                disabled={isLoading}
              />
            </div>

            {error && (
              <p className="text-red-400 text-sm mb-4 text-center">{error}</p>
            )}

            <button
              type="submit"
              disabled={isLoading || !password}
              className="w-full py-3 bg-purple-600 hover:bg-purple-700 disabled:bg-purple-600/50 text-white font-medium rounded-lg transition-colors"
            >
              {isLoading ? '認証中...' : 'ログイン'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  // Render children if authenticated
  return <>{children}</>;
};

export default DevPasswordGate;
